// Bags and backpacks, and replacing the procedural walkers with real characters.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { PI } from '../../lib/math.js';
import { pick, rnd } from '../../lib/random.js';
import { M } from '../../lib/factory.js';
import { mergeAny } from '../../world/facade-details.js';
import { walkers } from '../models.js';
import { toonify } from '../../core/optimise.js';
import { mixers } from './loader.js';
import { wq } from './retarget.js';
import { prepared } from './prepared.js';
import { build, crowd, dress, fatten, outfit, swimOutfit } from './outfits.js';

export function dressWalkers() {
  // things people carry: a shopping bag in one hand or a backpack. Placed while the model holds its idle pose,
  // then attached to the bone so they follow the arm swing / torso.
  const bagGeo = mergeAny([
    new RoundedBoxGeometry(0.13, 0.15, 0.05, 1, 0.008).translate(0, -0.075, 0),
    new THREE.TorusGeometry(0.03, 0.004, 4, 10, PI).translate(0, 0, 0),
  ]);
  // backpack with its two straps running over the shoulders and down the chest
  const packGeo = mergeAny([
    new RoundedBoxGeometry(0.15, 0.19, 0.07, 2, 0.025),
    ...[-1, 1].flatMap((sx) => [
      new THREE.BoxGeometry(0.016, 0.15, 0.008).translate(sx * 0.045, -0.01, 0.155),
      new THREE.BoxGeometry(0.016, 0.008, 0.16).translate(sx * 0.045, 0.07, 0.075),
    ]),
  ]);
  const carryMats = [0xc9a26a, 0xf2efe6, 0x2e9e6a, 0xd9534f, 0x2f3c55, 0x3a3a3a].map((c) => {
    const m = M(c, { flatShading: false, roughness: 0.8 });
    toonify(m);
    return m;
  });
  function accessorize(model, P) {
    const r = rnd();
    if (r > 0.42) return;
    const mx = new THREE.AnimationMixer(model);
    mx.clipAction(P.idle).play();
    mx.update(0);
    mx.stopAllAction();
    model.updateMatrixWorld(true);
    const b = (n) => model.getObjectByName(n),
      up = new THREE.Vector3(0, 1, 0),
      mq = wq(model);
    const item = new THREE.Mesh(r < 0.26 ? bagGeo : packGeo, pick(carryMats));
    model.add(item);
    if (r < 0.26) {
      const h = b(rnd() < 0.5 ? 'HandL' : 'HandR');
      item.position.copy(
        model.worldToLocal(
          h.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, -0.02, 0)),
        ),
      );
      item.quaternion.identity();
      item.scale.setScalar(1 / model.scale.x);
      h.attach(item);
    } else {
      const ch = b('Chest'),
        p = ch.getWorldPosition(new THREE.Vector3()),
        back = new THREE.Vector3(0, 0, -1).applyQuaternion(mq);
      item.position.copy(
        model.worldToLocal(p.addScaledVector(back, 0.085).addScaledVector(up, -0.03)),
      );
      item.quaternion.identity();
      item.scale.setScalar(1 / model.scale.x);
      ch.attach(item);
    }
  }
  walkers.forEach((w, i) => {
    const fem = rnd() < 0.5,
      P = w.beach ? prepared[fem ? 5 : 0] : crowd[i % crowd.length],
      model = dress(SkeletonUtils.clone(P.root), P, w.beach ? swimOutfit(fem) : outfit());
    model.scale.setScalar(P.k);
    model.position.y = P.y0 * P.k;
    model.traverse((o) => {
      if (o.isSkinnedMesh) {
        o.castShadow = true;
        o.receiveShadow = true;
      }
    });
    const mixer = new THREE.AnimationMixer(model);
    const walkA = mixer.clipAction(P.walk),
      idleA = mixer.clipAction(P.idle);
    walkA.play();
    idleA.play();
    walkA.time = rnd() * walkA.getClip().duration;
    idleA.setEffectiveWeight(0);
    w.g.clear();
    w.g.add(model);
    const [wd, ht, belly] = build();
    w.g.scale.set(wd, ht, wd);
    fatten(model, belly);
    if (!w.beach) accessorize(model, P);
    w.model = { mixer, walkA, idleA, head: model.getObjectByName('Head') };
    w.look = 0;
    mixers.push(w);
  });
}
